import React, { useEffect, useReducer, useState, useMemo } from 'react';
import { withTranslation } from 'react-i18next';
import {
  Form,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Table,
  Row,
  Col,
  Input,
} from 'reactstrap';

import { isNil } from 'lodash';
import { useFormik } from 'formik';
import Select from 'react-select';
import Flatpickr from 'react-flatpickr';
import Wizard from '../../../Components/Common/Wizard';
import CsvImporter from '../../../Components/Common/CsvImporter';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import CurrencyInput from '../../../Components/Common/CurrencyInput';
import {
  batchImportIncentive,
  getAllIncentiveGroups,
  validateIncentiveFile,
} from '../../../slices/thunks';
import { currencyFieldToNumber } from '../../../helpers/functions';
import { formatDate } from '../../../helpers/format';
import { formatCurrency } from '../../../helpers/format';
import { removeBomFromBinaryFile } from '../../../helpers/encode_file';
import CsvFieldsList from '../../../Components/Common/CsvFieldsList';

const initialValues = {
  cashback_amount: '',
  cashback_expires_at: '',
  incentive_group_id: '',
  fields: undefined,
  rows: 0,
};

const IncentiveImporter = ({ isOpen, toggle, onSuccess, t, tReady }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(1);
  const [groups, setGroups] = useState([]);
  const dispatch = useDispatch();

  const getAllGroups = async () => {
    const action = await dispatch(
      getAllIncentiveGroups({
        filters: {
          type: { in: ['user'] },
        },
      })
    );

    if (!action.payload || action.error) {
      return;
    }

    setGroups(
      action.payload.map((item) => ({
        label: item.name,
        value: item.id,
      }))
    );
  };

  const [payload, setPayload] = useReducer(
    (acc, item) => ({ ...acc, ...item }),
    initialValues
  );

  const [validationData, setValidationData] = useState(null);

  useEffect(() => {
    getAllGroups();
  }, [isOpen]);

  const detectCashbackAmountColumn = (columnFields, previewColumns) => {
    if (!columnFields || !previewColumns) return false;

    const hasCashbackAmountField = columnFields.some(
      (field) => field === 'cashbackamount'
    );

    if (hasCashbackAmountField) return true;

    const cashbackColumnNames = [
      'cashback_amount',
      'valor_do_cashback',
      'Valor do cashback',
      'valor cashback',
      'cashback',
      'valor',
      'amount',
      'valor_do_incentivo',
      'incentivo',
    ];

    return previewColumns.some((column) =>
      cashbackColumnNames.some((name) =>
        column.header?.toLowerCase().includes(name.toLowerCase())
      )
    );
  };

  const detectCashbackExpiresAtColumn = (columnFields, previewColumns) => {
    if (!columnFields || !previewColumns) return false;

    const hasCashbackExpiresAtField = columnFields.some(
      (field) => field === 'cashbackexpiresat'
    );

    if (hasCashbackExpiresAtField) return true;

    const expiresAtColumnNames = [
      'cashback_expires_at',
      'data_de_expiracao_do_cashback',
      'Data de expiração do cashback',
      'data_expiracao',
      'expiracao',
      'expires_at',
      'data_vencimento',
      'vencimento',
      'validade',
      'data_validade',
    ];

    return previewColumns.some((column) =>
      expiresAtColumnNames.some((name) =>
        column.header?.toLowerCase().includes(name.toLowerCase())
      )
    );
  };

  const handleValidationResult = (validationResult) => {
    if (validationResult?.data?.validationData) {
      setValidationData(validationResult.data.validationData);

      setPayload({
        validationData: validationResult.data.validationData,
        detectedColumns:
          validationResult.data.validationData.detectedColumns || [],
      });
    }
  };

  const hasCashbackAmount = useMemo(() => {
    if (validationData?.detectedColumns) {
      return (
        validationData.detectedColumns.includes('cashback_amount') ||
        validationData.detectedColumns.includes('valor_do_cashback') ||
        validationData.detectedColumns.includes('Valor do cashback')
      );
    }

    return detectCashbackAmountColumn(
      payload.columnFields,
      payload.preview?.columns
    );
  }, [validationData, payload.columnFields, payload.preview?.columns]);

  const hasCashbackExpiresAt = useMemo(() => {
    if (validationData?.detectedColumns) {
      return (
        validationData.detectedColumns.includes('cashback_expires_at') ||
        validationData.detectedColumns.includes(
          'data_de_expiracao_do_cashback'
        ) ||
        validationData.detectedColumns.includes('Data de expiração do cashback')
      );
    }

    return detectCashbackExpiresAtColumn(
      payload.columnFields,
      payload.preview?.columns
    );
  }, [validationData, payload.columnFields, payload.preview?.columns]);

  const handleFileValidation = async (file, data) => {
    if (!(file instanceof File || file instanceof Blob)) {
      return { error: true, message: 'Arquivo inválido!' };
    }

    const configurationParse = data?.columnFields?.reduce(
      (current, value, index) => {
        const column = data?.preview?.columns?.find(
          (val) => val.index === index
        );
        return {
          ...current,
          [value || '']: { column: column?.header },
        };
      },
      {}
    );

    try {
      const result = await dispatch(
        validateIncentiveFile({
          file,
          configuration_parse: JSON.stringify(configurationParse),
        })
      ).unwrap();
      handleValidationResult(result);
      return result;
    } catch (error) {
      return { error: true, message: error.message };
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: async () => {
      setIsLoading(true);

      try {
        const cleanFile = await removeBomFromBinaryFile(payload.file);

        const {
          columnFields,
          preview,
          cashback_amount,
          cashback_expires_at,
          incentive_group_id,
        } = payload;

        const configurationParse = columnFields.reduce(
          (currentvalue, value, index) => {
            const column = preview.columns.find((val) => val.index === index);
            return {
              ...currentvalue,
              [value || '']: { column: column?.header },
            };
          },
          {}
        );

        const data = {
          file: cleanFile,
          cashback_amount: cashback_amount
            ? currencyFieldToNumber(cashback_amount)
            : null,
          cashback_expires_at: cashback_expires_at
            ? moment(cashback_expires_at).toISOString()
            : null,
          incentive_group_id,
          configuration_parse: JSON.stringify(configurationParse),
        };

        const action = await dispatch(
          batchImportIncentive({
            ...data,
            successMessage: t('import-success'),
            errorMessage: t('import-error'),
          })
        );

        if (!action.payload.error) {
          resetState();
          onSuccess();
        }
      } finally {
        setIsLoading(false);
      }
    },
  });

  const resetState = () => {
    setTimeout(() => {
      setActiveTab(1);
      setPayload(initialValues);
      formik.resetForm();
    }, 1000);
    toggle();
  };

  return (
    <>
      <Modal
        centered
        isOpen={isOpen}
        size={activeTab >= 2 ? 'lg' : 'xl'}
        style={{ transition: 'all 0.2s ease' }}
        toggle={resetState}
        id="showModal"
      >
        <ModalHeader className="bg-info-subtle p-3" toggle={toggle}>
          {tReady && t('incentive-import-modal-heading')}
        </ModalHeader>

        <Form
          className="tablelist-form"
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
            return false;
          }}
        >
          <ModalBody>
            <Wizard
              showSteps
              activeTab={activeTab}
              tabs={[
                {
                  label: tReady && t('incentive-import-modal-step1-heading'),
                  component: (
                    <>
                      <div className="row">
                        <div className="col-md-6">
                          <CsvFieldsList
                            fields={[
                              {
                                labelKey: 'import-document-prop',
                                descriptionKey: 'document-field-description',
                                tooltipKey: 'document-tooltip',
                                required: true,
                              },
                              {
                                labelKey: 'import-name-prop',
                                tooltipKey: 'name-tooltip',
                                required: false,
                              },
                              {
                                labelKey: 'import-email-prop',
                                tooltipKey: 'email-tooltip',
                                required: false,
                              },
                              {
                                labelKey: 'import-birthdate-prop',
                                tooltipKey: 'birthdate-tooltip',
                                required: false,
                              },
                              {
                                labelKey: 'import-cellphone-prop',
                                tooltipKey: 'cellphone-tooltip',
                                required: false,
                              },
                              {
                                labelKey: 'import-cashbackamount-prop',
                                tooltipKey: 'cashback-amount-tooltip',
                                required: false,
                              },
                              {
                                labelKey: 'import-cashbackexpiresat-prop',
                                tooltipKey: 'cashback-expires-tooltip',
                                required: false,
                              },
                            ]}
                          />
                        </div>
                        <div className="col-md-6">
                          <h6>
                            {tReady &&
                              t('incentive-import-modal-step1-heading')}
                          </h6>
                          <p className="d-w-100 mb-3">
                            {tReady &&
                              t('incentive-import-modal-step1-description')}
                          </p>

                          <button
                            className="btn btn-outline-primary mb-4"
                            onClick={() =>
                              window.open(
                                '/templates/incentive-import-template.csv',
                                '_blank'
                              )
                            }
                          >
                            <i className="ri-download-line me-1"></i>
                            {t('download-template')}
                          </button>
                        </div>
                      </div>

                      {validationData?.errorCount > 0 && (
                        <div className="alert alert-danger d-flex justify-content-between align-items-center mb-3">
                          <div>
                            <strong>Erros de validação encontrados</strong>
                            <br />
                            {validationData.errorCount} erros encontrados em{' '}
                            {validationData.totalRows} linhas.
                            <br />
                            {validationData.validRows} linhas válidas,{' '}
                            {validationData.invalidRows} com problemas.
                            <br />
                            Por favor, baixe o arquivo de erros para verificar
                            os detalhes e corrigir os problemas.
                          </div>
                          {validationData?.errorFileUrl && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() =>
                                window.open(
                                  validationData.errorFileUrl,
                                  '_blank'
                                )
                              }
                            >
                              <i className="ri-download-line me-1"></i>
                              {t('download-errors')}
                            </button>
                          )}
                        </div>
                      )}

                      <CsvImporter
                        onClose={() => setActiveTab(2)}
                        onComplete={(data) => {
                          return setPayload({ ...data });
                        }}
                        dataHandler={(raw) => {
                          const rows = raw?.reduce(
                            (acc, i) => acc + i,
                            payload.rows
                          );
                          setPayload({ rows });
                        }}
                        // onFileValidation={(file, data) =>

                        // handleFileValidation(file, data)
                        // }
                        fields={[
                          {
                            name: 'document',
                            label: t('import-document-prop'),
                          },
                          {
                            name: 'name',
                            label: t('import-name-prop'),
                            optional: true,
                          },
                          {
                            name: 'email',
                            label: t('import-email-prop'),
                            optional: true,
                          },
                          {
                            name: 'birthdate',
                            label: t('import-birthdate-prop'),
                            optional: true,
                          },
                          {
                            name: 'cellphone',
                            label: t('import-cellphone-prop'),
                            optional: true,
                          },
                          {
                            name: 'cashbackamount',
                            label: t('import-cashbackamount-prop'),
                            optional: true,
                          },
                          {
                            name: 'cashbackexpiresat',
                            label: t('import-cashbackexpiresat-prop'),
                            optional: true,
                          },
                        ]}
                      />
                    </>
                  ),
                },

                {
                  label: tReady && t('incentive-import-modal-step2-heading'),
                  component: (
                    <>
                      <h6>
                        {tReady && t('incentive-import-modal-step2-heading')}
                      </h6>
                      <p className="d-w-100">
                        {tReady &&
                          t('incentive-import-modal-step2-description')}
                      </p>

                      <Row>
                        <Col lg={12}>
                          <div className="mb-3">
                            <Label
                              htmlFor="incentive_group_id"
                              className="form-label"
                            >
                              {t('incentive-import-cashback-incentive-group')}
                              <span className="text-danger">*</span>
                            </Label>
                            <Select
                              value={groups.find(
                                (option) =>
                                  option.value === payload.incentive_group_id
                              )}
                              onChange={(selectedOption) => {
                                setPayload({
                                  incentive_group_id: selectedOption?.value,
                                });
                              }}
                              options={groups}
                              id="incentive_group_id"
                              placeholder={t('select')}
                            />
                          </div>
                        </Col>

                        {!hasCashbackAmount && (
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                htmlFor="cashback_amount"
                                className="form-label"
                              >
                                {t('incentive-import-cashback-amount')}
                                <span className="text-danger">*</span>
                              </Label>
                              <CurrencyInput
                                className="form-control"
                                id="cashback_amount"
                                placeholder={t(
                                  'incentive-import-cashback-amount'
                                )}
                                value={payload.cashback_amount}
                                onChange={(value) => {
                                  setPayload({
                                    cashback_amount: value,
                                  });
                                }}
                              />
                            </div>
                          </Col>
                        )}

                        {!hasCashbackExpiresAt && (
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                htmlFor="cashback_expires_at"
                                className="form-label"
                              >
                                {t('incentive-import-cashback-expires-at')}
                                <span className="text-danger">*</span>
                              </Label>
                              <Flatpickr
                                className="form-control"
                                id="cashback_expires_at"
                                placeholder={t('select-date')}
                                options={{
                                  dateFormat: 'd/m/Y',
                                }}
                                value={payload.cashback_expires_at}
                                onChange={(date) => {
                                  setPayload({
                                    cashback_expires_at: date[0],
                                  });
                                }}
                              />
                            </div>
                          </Col>
                        )}
                      </Row>

                      <div className="d-flex justify-content-end mt-3">
                        <button
                          type="button"
                          className="btn btn-success"
                          onClick={() => setActiveTab(3)}
                          disabled={!payload.incentive_group_id}
                        >
                          {t('next') || 'Próximo'}
                        </button>
                      </div>
                    </>
                  ),
                },

                {
                  label: tReady && t('incentive-import-modal-step3-heading'),
                  component: (
                    <>
                      <h6>
                        {tReady && t('incentive-import-modal-step3-heading')}
                      </h6>
                      <p className="d-w-100">
                        {tReady &&
                          t('incentive-import-modal-step3-description')}
                      </p>

                      <Table bordered striped>
                        <tbody>
                          <tr>
                            <th style={{ width: '30%' }} scope="row">
                              {t('incentive-import-review-amount')}
                            </th>
                            <td>
                              {payload?.rows} {t('items')}
                            </td>
                          </tr>
                          {payload?.validationData?.totalIncentiveAmount && (
                            <tr>
                              <th style={{ width: '30%' }} scope="row">
                                {t('incentive-import-review-total-amount')}
                              </th>
                              <td>
                                {formatCurrency(
                                  payload?.validationData?.totalIncentiveAmount
                                )}
                              </td>
                            </tr>
                          )}
                          {payload?.cashback_amount && (
                            <tr>
                              <th style={{ width: '30%' }} scope="row">
                                {t('incentive-import-review-cashback-amount')}
                              </th>
                              <td>{payload?.cashback_amount}</td>
                            </tr>
                          )}
                          {payload?.cashback_expires_at && (
                            <tr>
                              <th style={{ width: '30%' }} scope="row">
                                {t(
                                  'incentive-import-review-cashback-expires-at'
                                )}
                              </th>
                              <td>
                                {payload?.cashback_expires_at &&
                                  formatDate(
                                    payload?.cashback_expires_at,
                                    'DD/MM/YYYY 23:59'
                                  )}
                              </td>
                            </tr>
                          )}

                          <tr>
                            <th style={{ width: '30%' }} scope="row">
                              {t(
                                'incentive-import-review-cashback-incentive-group'
                              )}
                            </th>
                            <td>
                              {payload?.incentive_group_id && (
                                <>
                                  {
                                    groups.find(
                                      (group) =>
                                        group.value ===
                                        payload.incentive_group_id
                                    )?.label
                                  }
                                </>
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </Table>

                      <div className="hstack gap-2 justify-content-end">
                        <button
                          type="button"
                          className="btn btn-light"
                          onClick={resetState}
                          disabled={isLoading}
                        >
                          {t('close')}
                        </button>

                        <button
                          type="submit"
                          className="btn btn-success"
                          disabled={isLoading}
                        >
                          {t('confirm')}
                        </button>
                      </div>
                    </>
                  ),
                },
              ]}
            />
          </ModalBody>
        </Form>
      </Modal>
    </>
  );
};

export default withTranslation()(IncentiveImporter);
